import { useState } from 'react'
import { Link } from '@inertiajs/react'
import Layout from '@/components/Layout'
import { Section, Segments } from '@/components/ui'
import Avatar from '@/components/Avatar'
import { BarChart, Heatmap, dKey } from '@/components/Charts'
import StarterTemplates, { StarterUndoBar, type StarterTemplate } from '@/components/StarterTemplates'
import type { Stats } from '@/types'
import { t } from '@/lib/i18n'

interface LeaderRow { name: string; avatar_url: string | null; total: number; streak: number; is_current: boolean }
interface Props {
  uuid: string
  stats: Stats
  daily: Record<string, number>
  leaderboard: LeaderRow[]
  badges_earned: number
  badges_total: number
  has_data: boolean
  starter_templates: StarterTemplate[]
}

type Range = '30' | '14' | '7'

/** 稿子的 `.panel`：一块内容 + 一条 15px 小标题，右侧可挂一句提示或一个动作。 */
function Panel({ title, hint, children }: { title: string; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="mx-3 mt-4 px-3.5 py-4 bg-card rounded-card shadow-card">
      <header className="flex items-center gap-2 mb-3.5">
        <h2 className="m-0 text-[15px] font-bold">{title}</h2>
        {hint && <span className="ml-auto text-xs text-faint">{hint}</span>}
      </header>
      {children}
    </section>
  )
}

export default function Index({ uuid, stats, daily, leaderboard, badges_earned, badges_total, has_data, starter_templates }: Props) {
  const [copied, setCopied] = useState(false)
  const [range, setRange] = useState<Range>('30')
  const [picked, setPicked] = useState<string | null>(null)

  const today = dKey(new Date())
  const pickedDay = picked ?? today
  const pickedValue = daily[pickedDay] || 0

  // 「▲ 本周 +38」从 daily 客户端算，不加查询。0 时不显示 ——
  // 「本周 +0」是一句让人泄气且没有信息的话。
  const weekDelta = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - i)
    return daily[dKey(d)] || 0
  }).reduce((a, b) => a + b, 0)

  const share = async () => {
    const url = `${window.location.origin}/u/${uuid}`
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  const topTotal = Math.max(1, ...leaderboard.map((r) => r.total))

  return (
    <Layout bleed>
      <StarterUndoBar />

      {/* 稿子的 `.heroCard`：一张主卡取代原来 6 张等权重统计卡 ——
          六个数并列等于没有重点，而人来看板第一个问题就是「我有多少分」。 */}
      <section className="mx-3 mt-3 p-[18px] rounded-card text-white bg-gradient-to-br from-brand to-brand2 shadow-hero">
        <div className="text-[13px] font-semibold opacity-90">💰 {t('dashboard.stats.balance')}</div>
        <p className="m-0 mt-0.5 text-[44px] font-extrabold leading-[1.05]">{stats.balance}</p>
        {weekDelta > 0 && (
          <span className="inline-flex items-center gap-1 mt-2 px-[9px] py-[3px] rounded-full bg-white/20 text-xs font-bold">
            ▲ {t('dashboard.week_delta', { count: weekDelta })}
          </span>
        )}
        <div className="flex gap-2 mt-3.5">
          <Link href="/checkin" className="flex-1 min-h-tap rounded-ctl bg-white/20 flex items-center justify-center gap-1.5 font-bold text-sm active:bg-white/30">
            ✅ {t('dashboard.go_checkin')}
          </Link>
          <Link href="/store" className="flex-1 min-h-tap rounded-ctl bg-white/20 flex items-center justify-center gap-1.5 font-bold text-sm active:bg-white/30">
            🛍️ {t('dashboard.go_store')}
          </Link>
        </div>
      </section>

      {/* 稿子的 `.mini`：一行三个次要指标 */}
      <div className="grid grid-cols-3 gap-2 mx-3 mt-2">
        <MiniStat value={stats.total} label={`📈 ${t('dashboard.stats.total')}`} />
        <MiniStat value={<>{stats.streak} {stats.streak > 0 ? '🔥' : ''}</>} label={t('dashboard.stats.streak')} color="text-amber" />
        <MiniStat value={stats.earns} label={`✅ ${t('dashboard.stats.earns')}`} color="text-green" />
      </div>

      {/* 达成目标数与徽章计数稿子里没有，但它们是既有内容，不能做丢 ——
          压成一行小字比再占两张卡合适。 */}
      <p className="mx-3 mt-2 px-1 text-xs text-faint">
        {t('dashboard.secondary_line', { goals: stats.goals_done, badges: `${badges_earned}/${badges_total}` })}
      </p>

      {!has_data ? (
        // 没有数据时不渲染趋势 / 热力图 / 排行：空图表只会让新用户以为产品坏了。
        <div className="mx-3 mt-4">
          <StarterTemplates templates={starter_templates} />
        </div>
      ) : (
        <>
          <Panel title={`📈 ${t('dashboard.trend')}`} hint={t('dashboard.trend_hint')}>
            <div className="mb-3.5">
              <Segments<Range>
                label={t('dashboard.range_label')}
                value={range}
                onChange={setRange}
                options={[
                  { value: '30', label: t('dashboard.range_30') },
                  { value: '14', label: t('dashboard.range_14') },
                  { value: '7', label: t('dashboard.range_7') },
                ]}
              />
            </div>
            <BarChart daily={daily} days={Number(range)} selected={pickedDay} onSelect={setPicked} />
            <div className="flex items-baseline gap-2 mt-3 pt-3 border-t border-line">
              <span className="text-[13px] text-muted">
                {pickedDay === today ? t('dashboard.readout_today') : t('dashboard.readout_date', { date: pickedDay.slice(5) })}
              </span>
              <span className="ml-auto text-[19px] font-extrabold">+{pickedValue}</span>
            </div>
          </Panel>

          <Panel title={`🗓️ ${t('dashboard.heatmap')}`}>
            <Heatmap daily={daily} />
          </Panel>

          <Panel
            title={`🏆 ${t('dashboard.leaderboard')}`}
            hint={
              <button type="button" onClick={share} className="min-h-tap px-1 text-brand font-bold text-xs">
                {copied ? `✅ ${t('dashboard.link_copied')}` : `🔗 ${t('dashboard.share')}`}
              </button>
            }
          >
            {leaderboard.map((r, i) => (
              <div key={i} className="flex items-center gap-3 min-h-[60px] py-2 border-b border-line last:border-b-0">
                <span className={'w-[22px] shrink-0 text-center font-extrabold text-sm ' + (i === 0 ? 'text-amber' : 'text-faint')}>
                  {i + 1}
                </span>
                {/* 排行榜是「区分多个孩子」最吃力的一屏：三行纯文字里认名字，
                    比认脸慢。头像放在名次与名字之间，不改这一行的其余布局。 */}
                <Avatar name={r.name} url={r.avatar_url} size={32} />
                <span className="flex-1 min-w-0">
                  <b className={'block text-[15px] ' + (r.is_current ? 'font-extrabold' : 'font-semibold')}>
                    {r.name}{r.is_current ? ` ${t('dashboard.you')}` : ''}
                  </b>
                  <span className="block h-1.5 mt-1.5 rounded-full bg-sunk overflow-hidden">
                    <span
                      className="block h-full rounded-full bg-gradient-to-r from-brand to-brand2"
                      style={{ width: `${Math.round((r.total / topTotal) * 100)}%` }}
                    />
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <b className="block text-base font-extrabold">{r.total}</b>
                  <span className="block text-[11px] text-faint">🔥 {t('dashboard.days', { count: r.streak })}</span>
                </span>
              </div>
            ))}
          </Panel>
        </>
      )}
    </Layout>
  )
}

function MiniStat({ value, label, color = '' }: { value: React.ReactNode; label: string; color?: string }) {
  return (
    <div className="bg-card rounded-ctl px-2.5 py-3 text-center shadow-card">
      <div className={'text-xl font-extrabold leading-[1.1] ' + color}>{value}</div>
      <div className="text-[11px] text-muted mt-0.5">{label}</div>
    </div>
  )
}
